import { useMemo } from 'react';
import { processErrorMessage } from '../../utils/errorMessageProcessing';
import { extractRawErrorMessage, getBriefErrorSummary } from '../../utils/errorTransformation';
import type { ProcessedError } from './ErrorAlert.types';

type UseProcessedErrorsResult = {
  processedErrors: ProcessedError[];
  hasAnyAdditionalLines: boolean;
  briefSummary: string;
  shouldIncludeBriefSummary: boolean;
};

/**
 * Custom hook that processes error(s) into a structured format for display.
 * Handles error normalization, message processing, brief summary generation,
 * and deduplication logic for the ErrorAlert component.
 */
export const useProcessedErrors = (error?: unknown, errors?: unknown[]): UseProcessedErrorsResult =>
  useMemo(() => {
    const errorList = (() => {
      if (errors?.length) return errors;
      if (error)
        return [
          error,
        ];
      return [];
    })();

    if (errorList.length === 0) {
      return {
        briefSummary: '',
        hasAnyAdditionalLines: false,
        processedErrors: [],
        shouldIncludeBriefSummary: false,
      };
    }

    const processedErrors = errorList.map((err) => {
      const rawMessage = extractRawErrorMessage(err);
      return processErrorMessage(rawMessage);
    });

    const briefSummary = errorList.length === 1 ? getBriefErrorSummary(errorList[0]) : `${errorList.length} errors occurred`;

    const hasAnyAdditionalLines = processedErrors.some((err) => err.hasMultipleLines);

    const briefMatchesFirstLine = processedErrors.length === 1 && processedErrors[0].firstLine.trim() === briefSummary.trim();

    return {
      briefSummary,
      hasAnyAdditionalLines,
      processedErrors,
      shouldIncludeBriefSummary: !briefMatchesFirstLine,
    };
  }, [
    error,
    errors,
  ]);
